import { useTranslation } from 'react-i18next';
import type { GroupFormData } from '../shared/GroupFormConfig';
import GroupFormContainer from '../shared/GroupFormContainer';
import type { CreateGroupMutation, CreateGroupMutationVariables } from './CreateGroup.api';
import { CreateGroupDocument } from './CreateGroup.api';
import client from '@/apolloClient';
import '@/utils/notifications';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface CreateGroupProps {
    open: boolean;
    companyId: string;
    onClose: () => void;
    onCreated: () => void;
}

const CreateGroup = ({ open, onClose, onCreated, companyId }: CreateGroupProps) => {
    const { t } = useTranslation('groupDetails');

    const handleSubmit = async (formData: GroupFormData) => {
        try {
            const variables: CreateGroupMutationVariables = {
                input: { ...formData, companyId: companyId || '' },
            };

            const { data } = await client.mutate<CreateGroupMutation>({
                mutation: CreateGroupDocument,
                variables,
            });

            if (data?.createGroup?.__typename === 'CreateGroupSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.successTitle'),
                    description: t('notifications.successDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.createGroup?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.createGroup.fields,
                };
                showErrorNotification({
                    message: t('notifications.errorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.errorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating group');
        } catch (error) {
            console.error('Error creating group:', error);
            // Handle error appropriately
        }
    };
    return (
        <GroupFormContainer
            mode="create"
            companyId={companyId}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            onOpenChange={onClose}
            title={t('modalCreateGroup.title')}
        />
    );
};

export default CreateGroup;
